import Yup from 'src/utils/yup';
import errors from 'src/static/errors.json';

type ProfileFormSchemaField =
  | 'firstName'
  | 'lastName'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'subdivision'
  | 'country'
  | 'postalCode'
  | 'birthday'
  | 'gender'
  | 'sex'
  | 'ethnicity'
  | 'religion'
  | 'usCitizenOrResident'
  | 'lunchPlan'
  | 'isFirstGen'
  | 'currentInstitution';

export type ProfileFormSchemaFields = ProfileFormSchemaField[];

interface ProfileFormSchemaOptions {
  currentInstitutionType?: 'high-school' | 'college';
}

export const profileFormSchemaCreator = (
  fields: ProfileFormSchemaFields,
  {currentInstitutionType = 'high-school'}: ProfileFormSchemaOptions
) => {
  const schemaMap: Record<string, Yup.Schema<$TSFixMe>> = {
    firstName: Yup.string()
      .required(errors.validation.firstName.required)
      .matches(/[a-zA-Z]/, errors.validation.firstName.invalid),
    lastName: Yup.string()
      .required(errors.validation.lastName.required)
      .matches(/[a-zA-Z]/, errors.validation.lastName.invalid),
    currentInstitution:
      currentInstitutionType === 'high-school' || currentInstitutionType === 'college'
        ? Yup.object().test(
            'current-institution-parent-id-required',
            errors.validation.currentInstitution.required,
            function () {
              const {currentInstitution} = this.parent;

              if (!currentInstitution.parentId) {
                return this.createError({
                  path: 'currentInstitution',
                  message: errors.validation.currentInstitution.required,
                });
              }
              return true;
            }
          )
        : Yup.object().shape({
            campus: Yup.string()
              .nullable()
              .test('campus-required', 'Campus is required', function (value) {
                const {campuses} = this.parent;
                if (campuses && campuses.length > 0 && !value) {
                  return this.createError({
                    path: 'currentInstitution.campus',
                    message: 'Please choose a campus',
                  });
                }
                return true;
              }),
            campuses: Yup.array().of(Yup.string()).nullable(),
          }),
    addressLine1: Yup.string().required(errors.validation.address.addressLine1.required).nullable(),
    addressLine2: Yup.string().nullable(),
    city: Yup.string().required(errors.validation.address.city.required).nullable(),
    subdivision: Yup.string().required(errors.validation.address.subdivision.required).nullable(),
    country: Yup.string().required(errors.validation.address.country.required),
    postalCode: Yup.string()
      .required(errors.validation.address.postalCode.required)
      .nullable()
      .test('us-postal-code', errors.validation.address.postalCode.usFormat, function (value) {
        const {country} = this.parent;
        if (country === 'US') {
          return Yup.string().usPostalCode().isValidSync(value);
        }
        return true;
      }),
    birthdayDay: Yup.number().required(errors.validation.birthday.required),
    birthdayMonth: Yup.number().required(errors.validation.birthday.required),
    birthdayYear: Yup.number().min(1900).required(errors.validation.birthday.required),
    gender: Yup.string().nullable(),
    customGenderIdentity: Yup.string().nullable(),
    sex: Yup.string().nullable(),
    ethnicity: Yup.string().nullable(),
    religion: Yup.string().nullable(),
    customReligion: Yup.string().when('religion', {
      is: (religion: string) => religion?.includes('Other'),
      then: Yup.string().required(errors.validation.religion.customReligionRequired).nullable(),
      otherwise: Yup.string().nullable(),
    }),
    usCitizenOrResident: Yup.mixed().oneOf(['true', 'false', 'decline']),
    lunchPlan: Yup.string().nullable(),
    isFirstGen: Yup.string().nullable(),
  };

  // Allow grouping of inputs that use multiple fields
  const expandedFields = fields.flatMap((field) => {
    if (field === 'birthday') {
      return ['birthdayMonth', 'birthdayDay', 'birthdayYear'];
    }
    if (field === 'religion') {
      return ['religion', 'customReligion'];
    }
    if (field === 'gender') {
      return ['gender', 'customGenderIdentity'];
    }
    return field;
  });

  const selectedSchema: Record<string, Yup.Schema<$TSFixMe>> = {};
  expandedFields.forEach((field) => {
    if (schemaMap[field]) {
      selectedSchema[field] = schemaMap[field];
    }
  });

  return Yup.object().shape(selectedSchema);
};
